import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { IMAGES } from '../../shared/constant';
import { AuthService, AuthSessionState } from '../auth/auth.service';
import { GlobalSearchService } from '../services/global-search.service';
import { CallListService } from '../services/call-list.service';
import { KillSwitchService } from '../services/kill-switch.service';
import {
  KillSwitchConfirmDialogComponent,
  KillSwitchConfirmDialogData
} from './kill-switch-confirm-dialog/kill-switch-confirm-dialog.component';
import { interval, Subscription, of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-header',
  standalone: false,
  templateUrl: './header.component.html',
  styleUrl: './header.component.css'
})
export class HeaderComponent implements OnInit, OnDestroy {

  iconImages = IMAGES;
  convOpen = false;
  user: AuthSessionState | null = null;
  queueCount: number = 0;

  /**
   * Master AI outbound switch.
   *   aiActive = true  → AI is making outbound calls (toggle ON).
   *   aiActive = false → AI is paused / kill switch engaged (toggle OFF).
   * Backend field (#19028, pending) `isEnabled` is treated as "AI outbound enabled" === aiActive.
   */
  aiActive = false;
  killSwitchLoading = false;

  private queueCountSubscription?: Subscription;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly router: Router,
    private readonly globalSearchService: GlobalSearchService,
    private readonly authService: AuthService,
    private readonly callListService: CallListService,
    private readonly killSwitchService: KillSwitchService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar
  ) {

  }

  ngOnInit(): void {
    this.authService.user$().pipe(takeUntil(this.destroy$)).subscribe(u => {
      this.user = u;
      if (u?.isAuthenticated) {
        this.loadQueueCount();
        this.startQueueCountPolling();
        this.loadKillSwitchState();
      } else {
        this.stopQueueCountPolling();
        this.queueCount = 0;
        this.aiActive = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopQueueCountPolling();
  }

  private startQueueCountPolling(): void {
    this.stopQueueCountPolling();
    this.queueCountSubscription = interval(5 * 60 * 1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.user?.isAuthenticated) {
          this.loadQueueCount();
        }
      });
  }

  private stopQueueCountPolling(): void {
    if (this.queueCountSubscription) {
      this.queueCountSubscription.unsubscribe();
      this.queueCountSubscription = undefined;
    }
  }

  private loadQueueCount(): void {
    if (!this.user?.isAuthenticated) {
      return;
    }
    this.callListService.getCallQueueCount().pipe(
      catchError(error => {
        console.error('Error loading queue count:', error);
        return of(0);
      }),
      takeUntil(this.destroy$)
    ).subscribe(count => {
      this.queueCount = count;
    });
  }

  private loadKillSwitchState(): void {
    this.killSwitchLoading = true;
    this.killSwitchService.getMasterState().pipe(
      catchError(error => {
        console.error('[Header] Failed to load kill-switch state:', error);
        return of(false);
      }),
      takeUntil(this.destroy$)
    ).subscribe(state => {
      this.aiActive = !!state;
      this.killSwitchLoading = false;
    });
  }

  /**
   * #19019 — toggle change opens a confirmation dialog before any backend call.
   * Visual state stays at the previous value until the server confirms the change.
   * #19020 — on POST failure: keep previous state and surface MatSnackBar error.
   */
  onKillSwitchToggle(event: MatSlideToggleChange): void {
    const previousState = this.aiActive;
    const targetState = event.checked;

    // Revert visual immediately — actual flip happens only after server responds.
    event.source.checked = previousState;
    this.aiActive = previousState;

    if (targetState === previousState) {
      return;
    }

    const dialogRef = this.dialog.open<
      KillSwitchConfirmDialogComponent,
      KillSwitchConfirmDialogData,
      boolean
    >(KillSwitchConfirmDialogComponent, {
      data: { targetAiActive: targetState },
      disableClose: true,
      autoFocus: true,
      restoreFocus: true,
      panelClass: 'kill-switch-confirm-panel'
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
      if (!confirmed) {
        return;
      }
      this.applyKillSwitchChange(targetState, previousState);
    });
  }

  private applyKillSwitchChange(targetState: boolean, previousState: boolean): void {
    this.killSwitchLoading = true;

    this.killSwitchService.setMasterState(targetState).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: serverState => {
        this.aiActive = serverState;
        this.killSwitchLoading = false;
        this.snackBar.open(
          serverState ? 'AI outbound calls resumed.' : 'AI outbound calls stopped.',
          'Close',
          { duration: 3000, panelClass: ['kill-switch-snackbar'] }
        );
      },
      error: err => {
        console.error('[Header] Failed to update kill-switch state:', err);
        this.aiActive = previousState;
        this.killSwitchLoading = false;
        const action = targetState ? 'resume' : 'stop';
        const detail = this.extractErrorMessage(err);
        this.snackBar.open(
          `Could not ${action} AI outbound calls${detail ? ': ' + detail : ''}.`,
          'Retry',
          { duration: 6000, panelClass: ['kill-switch-snackbar', 'kill-switch-snackbar--error'] }
        ).onAction().pipe(takeUntil(this.destroy$)).subscribe(() => {
          this.applyKillSwitchChange(targetState, previousState);
        });
      }
    });
  }

  private extractErrorMessage(err: any): string {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (err.error && typeof err.error === 'string') return err.error;
    if (err.error?.message && typeof err.error.message === 'string') return err.error.message;
    if (err.message && typeof err.message === 'string') return err.message;
    if (typeof err.status === 'number') return `HTTP ${err.status}`;
    return '';
  }

  isActive(route: string): boolean {
    return this.router.url === route;
  }

  isConversationalActive(): boolean {
    const conversationalRoutes = ['/call-list', '/dashboard', '/agents-catalogs', '/killswitch-logs'];
    return conversationalRoutes.includes(this.router.url);
  }

  onGlobalSearch(event: Event): void {
    const term = (event.target as HTMLInputElement).value || '';
    this.globalSearchService.setSearchTerm(term);
  }

  logout(): void {
    this.authService.logout().catch(err => {
      console.error('[Header] Logout error:', err);
    });
  }

}
